import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useApiToken } from '../auth/AuthProvider';
import {
  listHistory, listReceipts, listUncategorizedReceipts, deleteReceipt,
  deleteReport, claimReceipts, getEmailProcessingStatus, ReceiptMetadata, ReportMetadata,
} from '../services/api';
import { deriveCategory, getCategoryStyle } from '../services/categoryUtils';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import ConfirmModal from '../components/ConfirmModal';
import LoadingAnimation from '../components/LoadingAnimation';

const DEV_AUTH_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';

const styles: Record<string, React.CSSProperties> = {
  welcome: { fontSize: 28, fontWeight: 700, color: '#1a365d', margin: '0 0 8px' },
  subtitle: { color: '#666', margin: '0 0 32px', fontSize: 15 },
  stats: { display: 'flex', gap: 20, marginBottom: 32 },
  stat: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '20px 24px', flex: 1 },
  statHighlight: { background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '20px 24px', flex: 1 },
  statValue: { fontSize: 32, fontWeight: 700, color: '#1a365d', margin: 0 },
  statHighlightValue: { fontSize: 32, fontWeight: 700, color: '#d97706', margin: 0 },
  statLabel: { fontSize: 13, color: '#666', margin: '4px 0 0' },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 18, fontWeight: 600, color: '#1a365d', margin: '0 0 12px' },
  receiptGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 },
  receiptCard: { border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: 'white', position: 'relative' as const, cursor: 'pointer' },
  previewBtn: { position: 'absolute' as const, top: 4, right: 4, background: 'rgba(255,255,255,0.92)', border: '1px solid #cbd5e0', padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: 'pointer', zIndex: 2, color: '#0078d4' },
  previewImg: { width: '100%', height: 105, objectFit: 'cover' as const, background: '#f7fafc' },
  imageFallback: { width: '100%', height: 105, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  cardInfo: { padding: '6px 8px' },
  cardDate: { fontSize: 11, fontWeight: 600, color: '#1a365d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  cardDetail: { fontSize: 10, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  cardAmount: { fontSize: 12, fontWeight: 700, color: '#38a169', marginTop: 1 },
  dismissBtn: { background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 10, padding: 0 },
  claimBtn: { background: '#0078d4', color: 'white', border: 'none', padding: '2px 8px', borderRadius: 3, fontSize: 10, cursor: 'pointer' },
  emptyState: { background: '#f7fafc', border: '1px dashed #cbd5e0', borderRadius: 8, padding: 40, textAlign: 'center' as const, color: '#666', fontSize: 14 },
  emptyEmail: { fontWeight: 600, color: '#0078d4' },
  primaryBtn: { background: '#0078d4', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 15, cursor: 'pointer', fontWeight: 600 },
  recentReports: { marginTop: 32 },
  recentTable: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  recentTh: { textAlign: 'left' as const, padding: '8px', background: '#f7fafc', borderBottom: '2px solid #e2e8f0', fontSize: 12, fontWeight: 600 },
  recentTd: { padding: '8px', borderBottom: '1px solid #edf2f7' },
};

function ReceiptCard({
  receipt, onDelete, onClaim, onPreview,
}: {
  receipt: ReceiptMetadata;
  onDelete: () => void;
  onClaim?: () => void;
  onPreview: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const fn = receipt.file_name.toLowerCase();
  const isPdf = fn.endsWith('.pdf') || fn.endsWith('.html');
  const isUnattributed = receipt.source === 'mailbox' && !receipt.sender_email;
  const category = deriveCategory(receipt);
  const cat = getCategoryStyle(category);

  return (
    <div style={{ ...styles.receiptCard, background: cat.cardBg, borderColor: cat.border }}>
      {/* Category label at top */}
      <div style={{ background: cat.bg, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: cat.text, letterSpacing: 0.3 }}>{cat.label}</span>
      </div>
      {/* Preview button */}
      <div style={styles.previewBtn} onClick={(e) => { e.stopPropagation(); onPreview(); }} title="Preview receipt">
        View
      </div>
      {isPdf || imgError ? (
        <div style={{...styles.imageFallback, background: cat.bg}} onClick={onPreview}>
          <span style={{ fontSize: 36 }}>{cat.icon}</span>
        </div>
      ) : (
        <img
          src={receipt.download_url}
          alt={receipt.vendor_name || cat.label}
          style={{...styles.previewImg, cursor: 'pointer'}}
          onClick={onPreview}
          onError={() => setImgError(true)}
        />
      )}
      <div style={styles.cardInfo}>
        <div style={styles.cardDate}>
          {receipt.is_directions ? (receipt.vendor_name || 'Directions') : (receipt.vendor_name || 'Unknown vendor')}
        </div>
        <div style={styles.cardDetail}>
          {receipt.receipt_date || 'Date pending'}
          {receipt.receipt_time && ` at ${receipt.receipt_time}`}
        </div>
        {receipt.is_directions && receipt.total_mileage != null && (
          <div style={{ fontSize: 10, fontWeight: 600, color: '#234e52' }}>{receipt.total_mileage} miles</div>
        )}
        {receipt.receipt_amount != null && (
          <div style={styles.cardAmount}>${receipt.receipt_amount.toFixed(2)}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
          {onClaim && isUnattributed && (
            <button style={styles.claimBtn} onClick={(e) => { e.stopPropagation(); onClaim(); }}>Claim</button>
          )}
          <button style={styles.dismissBtn} onClick={(e) => { e.stopPropagation(); onDelete(); }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function useSafeAccountName(): string {
  if (DEV_AUTH_BYPASS) return 'Dev';
  const { accounts } = useMsal();
  return accounts[0]?.name?.split(' ')[0] || 'there';
}

export default function DashboardPage() {
  const userName = useSafeAccountName();
  const getToken = useApiToken();
  const navigate = useNavigate();
  const [reportCount, setReportCount] = useState(0);
  const [receiptCount, setReceiptCount] = useState(0);
  const [uncategorized, setUncategorized] = useState<ReceiptMetadata[]>([]);
  const [recentReports, setRecentReports] = useState<ReportMetadata[]>([]);
  const [previewReceipt, setPreviewReceipt] = useState<ReceiptMetadata | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; action: () => void } | null>(null);
  const [emailProcessing, setEmailProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const loadData = useCallback(async () => {
    try {
      const token = await getToken();
      const [reports, receipts, uncat, processingStatus] = await Promise.all([
        listHistory(token),
        listReceipts(token),
        listUncategorizedReceipts(token),
        getEmailProcessingStatus(token).catch(() => ({ processing: false })),
      ]);
      setReportCount(reports.length);
      setReceiptCount(receipts.length);
      setUncategorized(uncat);
      setRecentReports(reports.slice(0, 3));
      setEmailProcessing(processingStatus.processing);
    } catch {
      // Stats are non-critical
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadData();
  }, [loadData]);

  // Auto-refresh uncategorized receipts and email processing status every 3 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const token = await getToken();
        const [uncat, processingStatus] = await Promise.all([
          listUncategorizedReceipts(token),
          getEmailProcessingStatus(token),
        ]);
        setUncategorized(prev => {
          const prevIds = prev.map(r => r.receipt_id).sort().join(',');
          const newIds = uncat.map(r => r.receipt_id).sort().join(',');
          return prevIds === newIds ? prev : uncat;
        });
        setEmailProcessing(processingStatus.processing);
      } catch {
        // Polling failures are non-critical
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [getToken]);

  const handleDelete = (receiptId: string) => {
    setConfirmAction({
      title: 'Delete Receipt',
      message: 'This action is permanent and cannot be undone. The receipt image will be permanently removed from the application.',
      action: async () => {
        try {
          const token = await getToken();
          await deleteReceipt(token, receiptId);
          setUncategorized((prev) => prev.filter((r) => r.receipt_id !== receiptId));
        } catch (e) {
          alert(`Failed to delete: ${e}`);
        }
        setConfirmAction(null);
      },
    });
  };

  const handleClaim = async (receiptId: string) => {
    try {
      const token = await getToken();
      const claimed = await claimReceipts(token, [receiptId]);
      if (claimed.length > 0) await loadData();
    } catch (e) {
      alert(`Failed to claim: ${e}`);
    }
  };

  const handleDeleteReport = (reportId: string) => {
    setConfirmAction({
      title: 'Delete Report',
      message: 'This action cannot be undone. All receipts within the report will be returned to Uncategorized Receipts.',
      action: async () => {
        try {
          const token = await getToken();
          await deleteReport(token, reportId);
          setRecentReports((prev) => prev.filter((r) => r.report_id !== reportId));
          setReportCount((prev) => prev - 1);
          loadData();
        } catch (e) {
          alert(`Delete failed: ${e}`);
        }
        setConfirmAction(null);
      },
    });
  };

  if (loading) return <LoadingAnimation />;

  return (
    <div>
      <style>{`@keyframes receiptSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      <h1 style={styles.welcome}>Welcome, {userName}</h1>
      <p style={styles.subtitle}>Manage your expense reports for Gusto reimbursement using your personal credit card. The ability to manage receipts for corporate card purchases will be in a future release.</p>

      <div style={styles.stats}>
        <div style={styles.stat}>
          <p style={styles.statValue}>{reportCount}</p>
          <p style={styles.statLabel}>Reports Generated</p>
        </div>
        <div style={styles.stat}>
          <p style={styles.statValue}>{receiptCount}</p>
          <p style={styles.statLabel}>Total Receipts</p>
        </div>
        <div style={uncategorized.length > 0 ? styles.statHighlight : styles.stat}>
          <p style={uncategorized.length > 0 ? styles.statHighlightValue : styles.statValue}>
            {uncategorized.length}
          </p>
          <p style={styles.statLabel}>Uncategorized Receipts</p>
        </div>
      </div>

      {/* New Expense Report button above receipts */}
      <div style={{ marginBottom: 20 }}>
        <button style={styles.primaryBtn} onClick={() => navigate('/new-report')}>
          New Expense Report
        </button>
      </div>

      <div style={styles.section}>
        {emailProcessing && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
            padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 13, color: '#1e40af',
          }}>
            <span style={{ display: 'inline-block', animation: 'receiptSpin 1s linear infinite', fontSize: 16 }}>&#9881;</span>
            Processing receipt from email...
          </div>
        )}
        <h2 style={styles.sectionTitle}>Uncategorized Receipts</h2>

        {uncategorized.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No uncategorized receipts.</p>
            <p>Forward receipts to <span style={styles.emptyEmail}>receipts@cultivategeospatial.com</span> and they will appear here automatically.</p>
          </div>
        ) : (
          <div style={styles.receiptGrid}>
            {uncategorized.map((r) => (
              <ReceiptCard
                key={r.receipt_id}
                receipt={r}
                onDelete={() => handleDelete(r.receipt_id)}
                onClaim={() => handleClaim(r.receipt_id)}
                onPreview={() => setPreviewReceipt(r)}
              />
            ))}
          </div>
        )}
      </div>

      {recentReports.length > 0 && (
        <div style={styles.recentReports}>
          <h2 style={styles.sectionTitle}>Recent Reports</h2>
          <table style={styles.recentTable}>
            <thead>
              <tr>
                <th style={styles.recentTh}>Title</th>
                <th style={styles.recentTh}>Date</th>
                <th style={styles.recentTh}>Period</th>
                <th style={styles.recentTh}>Total</th>
                <th style={styles.recentTh}>Receipts</th>
                <th style={styles.recentTh}></th>
              </tr>
            </thead>
            <tbody>
              {recentReports.map((r) => (
                <tr key={r.report_id}>
                  <td
                    style={{ ...styles.recentTd, fontWeight: 600, color: '#0078d4', cursor: 'pointer' }}
                    onClick={() => navigate(`/history/${r.report_id}`)}
                  >
                    {r.title || '(untitled)'}
                  </td>
                  <td style={styles.recentTd}>{new Date(r.generated_timestamp).toLocaleDateString()}</td>
                  <td style={styles.recentTd}>{r.date_range_start} to {r.date_range_end}</td>
                  <td style={styles.recentTd}>${r.total_amount.toFixed(2)}</td>
                  <td style={styles.recentTd}>{r.receipt_count}</td>
                  <td style={styles.recentTd}>
                    <button
                      style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 11 }}
                      onClick={() => handleDeleteReport(r.report_id)}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Receipt preview modal */}
      <ReceiptPreviewModal
        receipt={previewReceipt}
        onClose={() => setPreviewReceipt(null)}
        onUpdate={(updated) => {
          setUncategorized(prev => prev.map(r => r.receipt_id === updated.receipt_id ? updated : r));
          setPreviewReceipt(updated);
        }}
      />

      {/* Confirm modal */}
      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmLabel="Delete"
        danger
        onConfirm={() => confirmAction?.action()}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
